/* eslint-disable no-nested-ternary */
import React, {
  Fragment, useEffect, useMemo, useState,
} from 'react';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Alert,
  Drawer, Empty, Radio, Select, Table, Tabs,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faArrowUp, faArrowDown, faDownload,
} from '@fortawesome/free-solid-svg-icons';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';
import { LoadingOutlined } from '@ant-design/icons';
import ProductActions from '@Actions/productActions';
import H3Image from '@Uilib/h3Image';
import closeIcon from '@Images/icons/icon-close-blue.png';
import H3Text from '@Uilib/h3Text';
import './style.scss';
import documentUINamesConstant from '../../../../../apis/documentUINamesConstant';
import HideComponent from '../../../../Common/RestrictedAccess/HideComponent';
import PRZSelect from '../../../../Common/UI/PRZSelect';

const { Option } = Select;

/**
 *
 */
const RecentTransactions = ({
  tenantId, productSkuId, sellerId, getRecentTransactions, internalSkuCode, refProductCode, productSkuName,
  recentTransactions, getRecentTransactionsLoading, className, MONEY, fullTextButton, sellerName, costumerID, TransactionType, user, priceMasking,
}) => {
  const [call, setCall] = useState(false);
  const [showRecentTxn, setShowRecentTxn] = useState(false);
  const [selectedDays, setSelectedDays] = useState(90);
  const [typeOfTransaction, setTypeOfTransaction] = useState(TransactionType || 'PURCHASE_ORDER');
  const [selectedSeller, setSelectedSeller] = useState(sellerId || '');
  const [selectedCustomer, setSelectedCustomer] = useState(costumerID || '');
  const [sellerList, setSellerList] = useState([]);
  const [customerList, setCustomerList] = useState([]);

  const documentUINames = useMemo(() => documentUINamesConstant(user), [user]);

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  const poSeriesLine = [
    {
      name: 'Purchase Order Price',
      data: recentTransactions?.data?.map((item) => Number(item?.po_price)),
    }, {
      name: 'Vendor Invoice Price',
      data: recentTransactions?.data?.map((item) => Number(item?.grn_price)),
    },
    {
      name: 'Weighted Average Vendor Invoice Price',
      data: recentTransactions?.data?.map((item) => Number(item?.weighted_avg_price)),
    }];

  const soSeriesLine = [
    {
      name: `${documentUINames.salesOrderUIName} Price`,
      data: recentTransactions?.data?.map((item) => Number(item?.so_price)),
    }, {
      name: 'Invoice Price',
      data: recentTransactions?.data?.map((item) => Number(item?.inv_price)),
    }];

  const optionsLine = {
    chart: {
      id: 'chart',
      type: 'line',
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: [4, 4],
      curve: 'straight',
      dashArray: [4, 0],
    },
    title: {
      text: `#${internalSkuCode}${refProductCode ? ` - ${refProductCode}` : ''}`,
      align: 'left',
      style: {
        fontSize: '14px',
        fontWeight: '400',
        color: '#484848',
      },
    },
    markers: {
      size: 6,
      hover: {
        sizeOffset: 3,
      },
    },
    colors: ['#32B40D', '#008FFB', '#F5D22F'],
    xaxis: {
      type: 'datetime',
      tooltip: {
        enabled: false,
      },
      categories: recentTransactions?.data?.map((item) => dayjs(item?.grn_date || item?.inv_date || item?.so_date).format('YYYY-MM-DD HH:mm:ss')),
    },
    yaxis: {
      tickAmount: 8,
      tooltip: {
        enabled: false,
      },
      labels: {
        formatter(value) {
          return MONEY(value);
        },
      },
    },
  };

  useEffect(() => {
    if (showRecentTxn && typeOfTransaction === 'PURCHASE_ORDER' && !(isDataMaskingPolicyEnable && isHideCostPrice)) {
      getRecentTransactions({
        tenantId,
        productSkuId,
        sellerId: selectedSeller,
        rangeDays: selectedDays,
        costumerID: selectedCustomer,
        typeOfTransaction,
      }, () => {

      });
    } else if (showRecentTxn && typeOfTransaction === 'SALES_ORDER' && !(isDataMaskingPolicyEnable && isHideSellingPrice)) {
      getRecentTransactions({
        tenantId,
        productSkuId,
        sellerId: selectedSeller,
        rangeDays: selectedDays,
        costumerID: selectedCustomer,
        typeOfTransaction,
      }, () => {

      });
    }
  }, [tenantId, productSkuId, sellerId, call, showRecentTxn, selectedDays, typeOfTransaction, costumerID, selectedSeller, selectedCustomer]);

  useEffect(() => {
    if (showRecentTxn && typeOfTransaction === 'PURCHASE_ORDER') {
      const list = sellerList.length > 0 ? sellerList : (recentTransactions?.data || []).reduce((acc, curr) => (acc.filter((item) => item?.seller_id === curr?.seller_id).length > 0 ? acc : [...acc, curr]), []);

      setSellerList(list);
    } else if (showRecentTxn && typeOfTransaction === 'SALES_ORDER') {
      const list = customerList.length > 0 ? customerList : (recentTransactions?.data || []).reduce((acc, curr) => (acc.filter((item) => item?.customer_id === curr?.customer_id).length > 0 ? acc : [...acc, curr]), []);

      setCustomerList(list);
    }
  }, [recentTransactions, selectedDays]);

  const getPriceChange = () => {
    const firstPrice = Number(recentTransactions?.data?.[0]?.grn_price || recentTransactions?.data?.[0]?.inv_price || 0);
    const lastPrice = Number(recentTransactions?.data?.[recentTransactions?.data?.length - 1]?.grn_price || recentTransactions?.data?.[recentTransactions?.data?.length - 1]?.inv_price || 0);
    const priceDiff = lastPrice - firstPrice;
    return {
      value: priceDiff,
      percentage: (priceDiff / firstPrice) * 100,
    };
  };

  const getAveragePrice = () => {
    let total = 0;
    for (let i = 0; i < recentTransactions?.data?.length; i++) {
      total += Number(recentTransactions?.data?.[i]?.grn_price || recentTransactions?.data?.[i]?.inv_price);
    }
    return (total / recentTransactions?.data?.length).toFixed(2);
  };
  const poDataColumns = [
    // {
    //   title: 'INTERNAL SKU CODE',
    //   responsive: ['sm', 'md', 'lg', 'xxl'],
    //   render: (record) => (
    //     <div>
    //       {record?.internal_slr_code}
    //     </div>
    //   ),
    // },
    {
      title: 'VENDOR',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <Link to={`/vendors/view/${record?.seller_id}`} target="_blank">
          {`${record?.internal_slr_code} - ${record?.seller_name}` || '-'}
        </Link>
      ),
    },
    {
      title: '#GRN NUMBER',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <Link to={`/purchase/goods-receiving/view/${record?.grn_id}`} target="_blank">
          {`#${record?.grn_number}` || '-'}
        </Link>
      ),
    },
    {
      title: 'GRN DATE',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <div>
          {dayjs(record?.grn_date).format('DD/MM/YYYY') || '-'}
        </div>
      ),
    },
    {
      title: 'UNIT PRICE',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <div>
          {MONEY(record?.grn_price) || '-'}
        </div>
      ),
    },
  ];

  const soDataColumns = [
    {
      title: 'CUSTOMER',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <Link to={`/sales/customer/view/${record?.customer_id}`} target="_blank">
          {`${record?.customer_name}` || '-'}
        </Link>
      ),
    },
    {
      title: '#INVOICE NUMBER',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <Link to={`/sales/invoice/view/${record?.invoice_id}`} target="_blank">
          {`#${record?.invoice_number}` || '-'}
        </Link>
      ),
    },
    {
      title: 'INVOICE DATE',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <div>
          {dayjs(record?.inv_date).format('DD/MM/YYYY') || '-'}
        </div>
      ),
    },
    {
      title: 'UNIT PRICE',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <div>
          {MONEY(record?.inv_price) || '-'}
        </div>
      ),
    },
  ];

  const items = [
    {
      key: 'PURCHASE_ORDER',
      label: 'Purchase',
      // visible: !(isDataMaskingPolicyEnable && isHideCostPrice),
    },
    {
      key: 'SALES_ORDER',
      label: 'Sales',
      // visible: !(isDataMaskingPolicyEnable && isHideSellingPrice),
    },
  ];

  return (
    <Fragment>
      <div
        className={`recent-txn__button ${className}`}
        onClick={() => setShowRecentTxn(true)}
        style={{
          fontSize: fullTextButton ? '11px' : '14px', margin: fullTextButton ? '10px 0px' : '0px', ...fullTextButton ? { display: 'flex', alignItems: 'center', gap: '3px' } : {},
        }}
      >
        {fullTextButton ? 'Recent Transactions ' : ''}
        <FontAwesomeIcon icon={faChartLine} />
      </div>
      <Drawer
        open={showRecentTxn}
        onClose={() => setShowRecentTxn(false)}
        width="800px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '755px' }}>
            <H3Text text={`Recent Transactions${sellerName ? ` of ${sellerName}` : ''}`} className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowRecentTxn(false)} />
          </div>
        </div>
        <div className="recent-txn__wrapper">
          <div className="recent-txn__header">
            <H3Text text={`${internalSkuCode} - ${refProductCode || ''} ${productSkuName}`} className="recent-txn__header-title" />

            <div className="recent-txn__header-actions">

              {
                showRecentTxn && typeOfTransaction === 'PURCHASE_ORDER' && !(isDataMaskingPolicyEnable && isHideCostPrice) ? (
                  <PRZSelect
                    filterOption={false}
                    onChange={(value) => setSelectedSeller(value)}
                    value={selectedSeller}
                    className="recent-txn__header-action"
                    popupMatchSelectWidth={false}
                    style={{ width: '200px' }}
                  >
                    <Option key="Select Vendor" value="">-All Vendors-</Option>
                    {
                      sellerList.map((item) => <Option key={item?.seller_id} value={item?.seller_id}>{`${ item?.seller_name}`}</Option>)
                    }
                  </PRZSelect>
                ) : showRecentTxn && typeOfTransaction === 'SALES_ORDER' && !(isDataMaskingPolicyEnable && isHideSellingPrice) ? (
                  <PRZSelect
                    filterOption={false}
                    onChange={(value) => setSelectedCustomer(value)}
                    value={selectedCustomer}
                    className="recent-txn__header-action"
                    popupMatchSelectWidth={false}
                    style={{ width: '200px' }}
                  >
                    <Option key="Select Customer" value="">-All Customers-</Option>
                    {
                      customerList.map((item) => <Option key={item?.customer_id} value={item?.customer_id}>{`${ item?.customer_name}`}</Option>)
                    }
                  </PRZSelect>
                ) : ''
              }

            </div>

            <div className="recent-txn__header-actions">
              {/* <div className="recent-txn__header-action recent-txn__header-action-download"> */}
              {/*  <FontAwesomeIcon icon={faDownload} /> */}
              {/* </div> */}
              {!(isDataMaskingPolicyEnable && isHideSellingPrice && isHideCostPrice) && (
                <PRZSelect
                  filterOption={false}
                  onChange={(value) => setSelectedDays(value)}
                  value={selectedDays}
                  className="recent-txn__header-action"
                >
                  <Option key={30} value={30}>30 Days</Option>
                  <Option key={60} value={60}>60 Days</Option>
                  <Option key={90} value={90}>90 Days</Option>
                  <Option key={180} value={180}>6 Months</Option>
                  <Option key={365} value={365}>1 Year</Option>
                  <Option key={-1} value="">All Time</Option>
                </PRZSelect>
              )}
            </div>
          </div>
          <div style={{ marginTop: '-20px' }}>
            <Tabs
              defaultActiveKey={typeOfTransaction}
              onChange={(e) => setTypeOfTransaction(e)}
              items={items}
              // items={items.filter((data) => data.visible)}
            />
          </div>
          {((typeOfTransaction === 'PURCHASE_ORDER' && !(isDataMaskingPolicyEnable && isHideCostPrice)) || (typeOfTransaction === 'SALES_ORDER' && !(isDataMaskingPolicyEnable && isHideSellingPrice))) ? (
            <div>
              {recentTransactions?.data?.length > 0 ? (
                <div className="recent-txn__metrics">
                  <div className="ant-row">
                    <div className="ant-col-md-8">
                      <div className="recent-txn__metric" style={{ backgroundColor: '#f0e7ff' }}>
                        <H3Text text={MONEY(getAveragePrice())} className="recent-txn__metric-value" />
                        <H3Text text="Average Price" className="recent-txn__metric-label" />
                      </div>
                    </div>
                    <div className="ant-col-md-8">
                      <div className="recent-txn__metric" style={{ backgroundColor: '#ffe6bf' }}>
                        <H3Text
                          text={MONEY(recentTransactions?.data?.[recentTransactions?.data?.length - 1]?.grn_price || recentTransactions?.data?.[recentTransactions?.data?.length - 1]?.inv_price)}
                          className="recent-txn__metric-value"
                        />
                        <H3Text text="Last Purchase Price" className="recent-txn__metric-label" />
                      </div>
                    </div>
                    <div className="ant-col-md-8">
                      <div className="recent-txn__metric" style={{ backgroundColor: '#e9f7ff' }}>
                        <div className="flex-display">
                          <H3Text text={MONEY(getPriceChange()?.value)} className="recent-txn__metric-value" />
                          <H3Text
                            text={(
                              <div style={{ color: getPriceChange()?.value > 0 ? 'red' : 'green' }}>
                                {getPriceChange()?.value > 0 ? <FontAwesomeIcon icon={faArrowUp} />
                                  : <FontAwesomeIcon icon={faArrowDown} />}
                                {`${Math.abs(getPriceChange()?.percentage)?.toFixed(0)}%`}
                              </div>
                            )}
                            className="recent-txn__metric-pct"
                          />
                        </div>
                        <H3Text text="Price Change" className="recent-txn__metric-label" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <Fragment>
                  {
                    getRecentTransactionsLoading ? (
                      <div className="ant-row" style={{ width: 'calc(100% + 15px)', marginBottom: '15px' }}>
                        {[0, 1, 2]?.map((item) => (
                          <div className="ant-col-md-8" key={item}>
                            <div style={{ height: '75px', width: 'calc(100% - 15px)' }} className="loadingBlock" />
                          </div>
                        ))}
                      </div>
                    ) : ''
                  }
                </Fragment>
              )}
              <div className="recent-txn__chart" style={{ height: '270px' }}>
                {getRecentTransactionsLoading ? (
                  <div className="recent-txn__chart-loader">
                    <LoadingOutlined />
                    <H3Text text="Loading recent transactions.." className="" />
                  </div>
                ) : (
                  <Fragment>
                    {recentTransactions?.data?.length ? (
                      <ReactApexChart
                        options={optionsLine}
                        series={recentTransactions?.data[0]?.seller_id
                          ? poSeriesLine : soSeriesLine}
                        type="line"
                        height={250}
                      />
                    )
                      : (
                        <div className="recent-txn__chart-empty">
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            imageStyle={{ height: 60 }}
                            description={(
                              <span>
                                No transactions found for this product
                              </span>
                            )}
                          />
                        </div>
                      )}
                  </Fragment>
                )}
              </div>
              {recentTransactions?.data?.length > 0 && (
                <div
                  className="recent-txn__table"
                >
                  <Table
                    title={() => (
                      <div>{`${recentTransactions?.data?.length || 0} Transactions`}</div>
                    )}
                    size="small"
                    scroll="scroll"
                    columns={recentTransactions?.data[0]?.seller_id ? poDataColumns : soDataColumns}
                    dataSource={recentTransactions?.data || []}
                    pagination={recentTransactions?.data?.length > 5}
                    bordered
                    loading={getRecentTransactionsLoading}
                  />
                </div>
              )}
            </div>
          ) : (
            <div>
              <HideComponent message={"You don't have access to view recent transactions"} />
            </div>
          )}
        </div>
      </Drawer>
    </Fragment>
  );
};

const mapStateToProps = ({ UserReducers, ProductReducers }) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  getRecentTransactionsLoading: ProductReducers.getRecentTransactionsLoading,
  recentTransactions: ProductReducers.recentTransactions,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  getRecentTransactions: (payload, callback) => dispatch(ProductActions.getRecentTransactions(payload, callback)),
});

RecentTransactions.propTypes = {
  user: PropTypes.any,
  getRecentTransactions: PropTypes.func,
  getRecentTransactionsLoading: PropTypes.bool,
  recentTransactions: PropTypes.any,
  MONEY: PropTypes.func,
  tenantId: PropTypes.any,
  productSkuId: PropTypes.any,
  sellerId: PropTypes.any,
  internalSkuCode: PropTypes.any,
  refProductCode: PropTypes.any,
  productSkuName: PropTypes.any,
  className: PropTypes.any,
  fullTextButton: PropTypes.any,
  sellerName: PropTypes.any,
  costumerID: PropTypes.any,
  TransactionType: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(RecentTransactions));
